/**
 * SdsDataSheetPage — AI research result detail screen. Renders both Figma variants,
 * routed by result.sdsSource:
 *   DOCUMENT — "SDS Data sheet" (6429:2318): the selected SDS document + attributes.
 *   WEB      — "Web based" (6435:3993): web-researched attributes with per-value
 *              sources and the count of external sources analysed.
 * Reached from AiResearchCard's VIEW RESULTS.
 */

import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import { Button, Divider, Expander, Icon } from "@amzn/meridian";
import arrowLeftTokens from "@amzn/meridian-tokens/base/icon/arrow-left-large";
import documentTokens from "@amzn/meridian-tokens/base/icon/document";
import { ClassifyWasteProfileResponse, SdsAttribute } from "src/types/classificationTypes";
import { ContainerUsingFor, ItemFlowContext } from "src/types/containerTypes";
import { reclassifyWithAttributes } from "src/services/classificationService";
import { useAppSelector } from "src/store/store";
import PillButton from "../../UI/PillButton";
import { FIXED_SDS_ATTRIBUTES } from "./EditAttributesSheet";
import EditAttributesSheet from "./EditAttributesSheet";
import sdsDocIcon from "src/images/sds-document-icon.png";
import "./classification.scss";

const groupAttributesByName = (attributes: SdsAttribute[]): Record<string, SdsAttribute> => {
  const mapper: Record<string, SdsAttribute> = {};

  attributes.forEach((attr) => {
    mapper[attr.name] = attr;
  });
  return mapper;
};

const buildAttributesPayload = (
  overrides: Record<string, string>,
  originalAttributes: Record<string, SdsAttribute>,
): Record<string, string[]> => {
  const payload: Record<string, string[]> = {};

  FIXED_SDS_ATTRIBUTES.forEach(({ label }) => {
    const value = (overrides[label] ?? originalAttributes[label]?.value ?? "").trim();
    if (value !== "") payload[label] = [value];
  });

  return payload;
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)}KB`;
  return `${(kb / 1024).toFixed(1)}MB`;
}

const AttributeSources: React.FC<{ sources?: string[] }> = ({ sources }) => {
  const [expanded, setExpanded] = useState(false);
  if (!sources || sources.length === 0) return null;

  return (
    <Expander title="Sources" type="inline" open={expanded} onChange={setExpanded}>
      <Column spacing="100">
        {sources.map((url, i) => {
          let display = url;
          try {
            display = new URL(url).hostname.replace(/^www\./, "");
          } catch {}
          return (
            <a key={i} href={url} target="_blank" rel="noreferrer" className="web-attr-source">
              {display}
            </a>
          );
        })}
      </Column>
    </Expander>
  );
};

interface SdsDataSheetState {
  result: ClassifyWasteProfileResponse;
  asin: string;
  itemName?: string;
  isEligibleForDonation?: boolean;
  itemFlowContext: ItemFlowContext;
  donationCategoryIds: string[];
  savedOverrides?: Record<string, string>;
}

const SdsDataSheetPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { activeSiteCode } = useAppSelector((state) => state.user);

  const state = location.state as SdsDataSheetState | undefined;

  const [overrides, setOverrides] = useState<Record<string, string>>(state?.savedOverrides ?? {});
  const [sheetOpen, setSheetOpen] = useState(false);
  const [recommendedProfiles, setRecommendedProfiles] = useState<string[]>(
    state?.result?.recommendedWasteProfilesIds ?? [],
  );
  const [profileReasoning, setProfileReasoning] = useState<string | undefined>(
    state?.result?.reasoning,
  );
  const [reclassifyError, setReclassifyError] = useState<string | null>(null);
  const [isReclassifying, setIsReclassifying] = useState(false);

  if (!state?.result) {
    return (
      <Column
        width="100%"
        height="100vh"
        alignmentHorizontal="center"
        alignmentVertical="center"
        spacing="400"
      >
        <Text>No AI research results available.</Text>
        <PillButton text="Go Back" color="secondary" onClick={() => navigate(-1)} />
      </Column>
    );
  }

  const { result, asin, itemName, isEligibleForDonation, itemFlowContext, donationCategoryIds } =
    state;
  const { sdsDocument, sdsSource, sdsAttributes, externalSourcesAnalyzed } = result;
  const isWeb = sdsSource === "WEB";
  const isDocument = sdsSource === "DOCUMENT";
  const recommendationHeader = isWeb
    ? "Web Attribute Based Recommendation"
    : "SDS Attribute Based Recommendation";

  const webResearchedAttributes = groupAttributesByName(sdsAttributes ?? []);

  const handleSaveOverrides = async (newOverrides: Record<string, string>) => {
    const hasOverridesChanged = FIXED_SDS_ATTRIBUTES.some(
      ({ label }) => newOverrides[label] !== overrides[label],
    );

    setOverrides(newOverrides);
    setSheetOpen(false);
    navigate(".", { replace: true, state: { ...location.state, savedOverrides: newOverrides } });

    if (!hasOverridesChanged) {
      return;
    }

    const attributesPayloadForReclassification = buildAttributesPayload(
      newOverrides,
      webResearchedAttributes,
    );

    setIsReclassifying(true);
    setReclassifyError(null);
    try {
      const response = await reclassifyWithAttributes({
        asin,
        siteName: activeSiteCode,
        classificationId: result.classificationId,
        sdsAttributes: attributesPayloadForReclassification,
      });
      setRecommendedProfiles(response.recommendedWasteProfilesIds);
      setProfileReasoning(response.reasoning);
      navigate(".", {
        replace: true,
        state: {
          ...location.state,
          savedOverrides: newOverrides,
          result: {
            ...result,
            recommendedWasteProfilesIds: response.recommendedWasteProfilesIds,
            reasoning: response.reasoning,
          },
        },
      });
    } catch (err) {
      console.error("[SdsDataSheetPage] Reclassify failed:", err);
      setReclassifyError("Failed to reclassify with updated values. Please try again.");
    } finally {
      setIsReclassifying(false);
    }
  };

  const goToContainer = (usingFor: ContainerUsingFor) => {
    const updatedContext: ItemFlowContext =
      itemFlowContext.type === "classification" || itemFlowContext.type === "toteExtraItem"
        ? { ...itemFlowContext, recommendedWasteProfileIds: recommendedProfiles }
        : itemFlowContext;
    navigate("/tote/chooseContainer", {
      state: {
        itemFlowContext: updatedContext,
        wasteProfileIds: usingFor === "WASTE" ? recommendedProfiles : [],
        donationCategoryIds: usingFor === "DONATION" ? donationCategoryIds : [],
        usingFor,
      },
    });
  };

  const renderWebAttributeRows = () =>
    FIXED_SDS_ATTRIBUTES.map(({ label }, idx) => {
      const webAttribute = webResearchedAttributes[label];
      const webValue = webAttribute?.value ?? "";
      const userValue = overrides[label];
      const hasValue = webValue.trim() !== "";

      return (
        <React.Fragment key={label}>
          {idx > 0 && <Divider />}
          <Column spacing="200">
            <Row alignmentVertical="top" alignmentHorizontal="justify">
              <Text type="b200" color="secondary" className="web-attr-name">
                {label}
              </Text>
              {webAttribute && <AttributeSources sources={webAttribute.sources} />}
            </Row>
            <Row spacing="400" alignmentVertical="center">
              {hasValue ? (
                <Text type="b300">{webValue}</Text>
              ) : (
                <Text type="b200" color="secondary">
                  No data found
                </Text>
              )}
              <span className="web-attr-badge">Web Data</span>
            </Row>
            {userValue && (
              <Row spacing="400" alignmentVertical="center">
                <Text type="b300">{userValue}</Text>
                <span className="user-data-badge">User Data</span>
              </Row>
            )}
          </Column>
        </React.Fragment>
      );
    });

  return (
    <Column className="app-gradient-bg pwa-scrollable" spacing="450" spacingInset="450 450 600">
      <Row alignmentVertical="center" spacingInset="400 none">
        <Button type="icon" onClick={() => navigate(-1)}>
          <Icon color="success" tokens={arrowLeftTokens} />
        </Button>
        <Text type="h100">AI Assisted Analysis</Text>
      </Row>

      <Column spacing="none" spacingInset="none 200">
        {itemName && <Text type="h300">{itemName}</Text>}
        <Text type="b200" color="secondary">{`ASIN: ${asin}`}</Text>
      </Column>

      {isDocument && sdsDocument && (
        <Column className="sds-document-card" width="100%" spacingInset="400" spacing="300">
          <Row alignmentVertical="center" spacing="400">
            <img className="sds-document-card__icon" src={sdsDocIcon} alt="SDS document" />
            <Column spacing="none">
              <Text type="b300">{sdsDocument.fileName}</Text>
              <Text
                type="b200"
                color="secondary"
              >{`Size: ${formatFileSize(sdsDocument.fileSizeBytes)}`}</Text>
            </Column>
            <button
              className="sds-document-card__view"
              onClick={() => window.open(sdsDocument.url, "_blank", "noopener,noreferrer")}
            >
              VIEW
            </button>
          </Row>
          <Divider />
          <Row spacing="100" alignmentVertical="top">
            <Text type="b200" color="secondary">
              Source:
            </Text>
            <a
              className="sds-document-card__source"
              href={sdsDocument.url}
              target="_blank"
              rel="noreferrer"
            >
              {sdsDocument.url}
            </a>
          </Row>
        </Column>
      )}

      {isWeb && (
        <>
          <Column className="web-attributes-card" width="100%" spacingInset="400" spacing="300">
            <Text type="h300">Web based attributes value</Text>
            <Divider />
            {renderWebAttributeRows()}
            <Divider />
            <Column spacing="300">
              <Text type="b200" color="secondary">
                Do you have different reliable sources of attribute values?
              </Text>
              <button className="ai-external-search-btn" onClick={() => setSheetOpen(true)}>
                EDIT ATTRIBUTE VALUES
              </button>
            </Column>
          </Column>
          {externalSourcesAnalyzed != null && (
            <Text type="b200" color="secondary">
              {`${externalSourcesAnalyzed} External sources analysed`}
            </Text>
          )}
        </>
      )}

      {sdsAttributes && sdsAttributes.length > 0 && !isWeb && (
        <Column className="sds-attributes-card" width="100%" spacingInset="400" spacing="300">
          <Row width="100%" widths={["50%", "fill"]}>
            <Text type="h100">Attributes</Text>
            <Text type="h100">Values</Text>
          </Row>
          <Divider />
          {sdsAttributes.map((attribute, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && <Divider />}
              <Row width="100%" widths={["50%", "fill"]} alignmentVertical="top" spacing="300">
                <Text type="b200" color="secondary">
                  {attribute.name}
                </Text>
                <Text type="b200">{attribute.value}</Text>
              </Row>
            </React.Fragment>
          ))}
        </Column>
      )}

      <Column
        className={`home-card ${isWeb ? "asin-waste-profile" : "asin-waste-profile--sds"}`}
        width="100%"
        spacingInset="400 none none"
      >
        <Row alignmentVertical="center" spacing="300" spacingInset="none 500">
          <Icon color="inverted" tokens={documentTokens} />
          <Text type="h200" color="inverted">
            {recommendationHeader}
          </Text>
        </Row>
        <Column className="home-card" spacingInset="none">
          <Column spacingInset="400 500" spacing="300">
            {isReclassifying ? (
              <Text type="b200" color="secondary">
                Updating profile...
              </Text>
            ) : reclassifyError ? (
              <Text type="b200" color="error">
                {reclassifyError}
              </Text>
            ) : recommendedProfiles.length === 0 ? (
              <Text type="b200" color="secondary">
                No matching profile found based on your edited values.
              </Text>
            ) : (
              <>
                {recommendedProfiles.map((profileId, idx) => (
                  <React.Fragment key={profileId}>
                    {idx > 0 && <Divider />}
                    <Text type="h200">{profileId}</Text>
                  </React.Fragment>
                ))}
                {profileReasoning && (
                  <Text type="b200" color="secondary">
                    {profileReasoning}
                  </Text>
                )}
              </>
            )}
          </Column>
        </Column>
      </Column>

      <Column className="asin-sticky-footer" spacing="300">
        <Row spacing="300" alignmentHorizontal="center">
          <PillButton
            text="DONATE"
            color="outline"
            onClick={() => goToContainer("DONATION")}
            disabled={isEligibleForDonation !== true || isReclassifying}
          />
          <PillButton
            text="ADD TO WASTE"
            color="success"
            onClick={() => goToContainer("WASTE")}
            disabled={isReclassifying}
          />
        </Row>
      </Column>

      <EditAttributesSheet
        open={sheetOpen}
        sdsAttributes={sdsAttributes}
        savedOverrides={overrides}
        onSave={handleSaveOverrides}
        onCancel={() => setSheetOpen(false)}
      />
    </Column>
  );
};

export default SdsDataSheetPage;
